import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/compat/router';

import { ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';
import { getSessionPersistedValue, removePersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { ModalNames, useModals } from 'src/components/modals';
import { useLoyalty as useRewards } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { FindOrCreateReferralCodeDocument } from 'src/gql/graphql';
import type { FindOrCreateReferralCodeMutation } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import { isReferralsRoute } from './rewards/dpos-utils';
import { Integrations, Statuses, useLoyalty } from './use-loyalty';

const SESSION_KEY = 'storefrontReferral';

type ReferralSession = {
  dispensaryId: string;
  code: string;
  userId: string | null;
  prompted: boolean;
  attemptedCode: string | null;
  status: 'attempted' | 'closed' | 'pending';
};

type ResolvedReferral = {
  lookup: { key: string };
  data: FindOrCreateReferralCodeMutation['findOrCreateReferralCode'];
};

function readSession(): ReferralSession | null {
  const value: unknown = getSessionPersistedValue(SESSION_KEY);
  if (!value || typeof value !== 'object') {
    return null;
  }
  const session = value as Partial<ReferralSession>;
  return typeof session.dispensaryId === 'string' &&
    typeof session.code === 'string' &&
    !!session.code &&
    session.code !== '0' &&
    (session.userId === null || typeof session.userId === 'string') &&
    typeof session.prompted === 'boolean' &&
    (session.attemptedCode === null || typeof session.attemptedCode === 'string') &&
    ['pending', 'attempted', 'closed'].includes(session.status ?? '')
    ? (session as ReferralSession)
    : null;
}

/** Keep only the current store's code, never a trusted sender, for this storefront session. */
export function useStorefrontReferral(): void {
  const router = useRouter();
  const flags = useFlags();
  const { dispensary, loading: storeLoading, error: storeError } = useCurrentDispensary();
  const { dispensary: enrollmentDispensary } = useDispensary();
  const UI = useUI();
  const User = useUser();
  const { isLoggedIn, userId, userLoading } = useObserver(() => ({
    isLoggedIn: User.isLoggedIn,
    userId: User.isLoggedIn ? User.id : null,
    userLoading: User.loading,
  }));
  const { openModal } = useModals();
  const showErnie = useErnie();
  const onReferralRoute = router ? isReferralsRoute(router) : false;
  const onReferralLanding =
    router?.pathname === '/stores/[cName]/referral' || router?.pathname === '/stores/[cName]/referral/[code]';
  const rawCode =
    onReferralRoute &&
    router?.isReady &&
    router.pathname === '/stores/[cName]/referral/[code]' &&
    typeof router.query.code === 'string'
      ? router.query.code
      : '';
  const incomingCode = rawCode && rawCode !== '0' ? rawCode : '';
  const isStandardStorefront =
    !!router?.pathname.startsWith('/stores/') && !UI.isKiosk && !UI.isEmbedded && !UI.isMobileEcommApp;
  const dispensaryId = dispensary?.id;
  const lspName = dispensary?.dposLspName;
  const promptStorageKey = `storefrontReferralPrompt:${dispensaryId ?? ''}:${incomingCode}`;
  const attemptStorageKey = `storefrontReferralAttempt:${dispensaryId ?? ''}:${userId ?? ''}`;
  const storedAttempt: unknown = dispensaryId && userId ? getSessionPersistedValue(attemptStorageKey) : null;
  const priorAttemptedCode = typeof storedAttempt === 'string' ? storedAttempt : null;
  const storeResolved = !!dispensaryId && !storeLoading && !storeError;
  const enrollmentStoreReady = storeResolved && enrollmentDispensary?.id === dispensaryId;
  const supportsReferral =
    storeResolved &&
    !!dispensary.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled &&
    !!dispensary.storeSettings.showLoyaltyRewards &&
    !UI.isKiosk;
  const [session, setSession] = useState<ReferralSession | null>(null);
  const saveSession = useCallback((next: ReferralSession): void => {
    setPersistedValue(SESSION_KEY, next, true);
    setSession((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
  }, []);

  useEffect(() => {
    if (!router?.isReady) {
      return;
    }
    if (!isStandardStorefront) {
      removePersistedValue(SESSION_KEY, true);
      setSession(null);
      return;
    }
    if (!dispensaryId) {
      return;
    }
    let next = readSession();
    if (next && next.dispensaryId !== dispensaryId) {
      removePersistedValue(SESSION_KEY, true);
      next = null;
    }
    // The latest distinct explicit code wins; revisiting the same code never reopens it.
    if (incomingCode && incomingCode !== next?.code) {
      const attemptedCode = priorAttemptedCode ?? (next?.userId === userId ? next?.attemptedCode ?? null : null);
      next = {
        dispensaryId,
        code: incomingCode,
        userId: null,
        prompted: getSessionPersistedValue(promptStorageKey) === true,
        attemptedCode,
        status: attemptedCode ? 'closed' : 'pending',
      };
    }
    if (!next) {
      setSession(null);
      return;
    }
    if (!userLoading) {
      if (next.userId !== null && next.userId !== userId) {
        next = { ...next, status: 'closed' };
      } else if (isLoggedIn && userId) {
        next = { ...next, userId };
      }
    }
    if (storeResolved && !supportsReferral) {
      next = { ...next, status: 'closed' };
    }
    if (incomingCode && !userLoading && !isLoggedIn && !next.prompted) {
      next = { ...next, prompted: true };
      setPersistedValue(promptStorageKey, true, true);
      saveSession(next);
      void openModal(ModalNames.signUp);
      return;
    }
    saveSession(next);
  }, [
    dispensaryId,
    router?.isReady,
    isStandardStorefront,
    incomingCode,
    promptStorageKey,
    priorAttemptedCode,
    userLoading,
    userId,
    isLoggedIn,
    storeResolved,
    supportsReferral,
    openModal,
    saveSession,
  ]);

  const matchesSession =
    session?.dispensaryId === dispensaryId &&
    session?.userId === userId &&
    (!incomingCode || incomingCode === session?.code);
  const referralCode = matchesSession ? session?.code ?? '' : '';
  const key = `${userId ?? ''}:${dispensaryId ?? ''}:${referralCode}`;
  const rewards = useRewards(undefined, undefined, undefined, { skip: !onReferralLanding && !referralCode });
  const rewardsSettled = enrollmentStoreReady && rewards.rewardsResolved && !rewards.loading && !rewards.hasError;
  const hasNativeProfile = rewards.isLoyaltyMember || !!rewards.posCustomerId;
  const attemptedKeyRef = useRef<string | null>(null);
  const warnedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // Keep the bare/legacy referral landing's existing signup behavior.
    if (onReferralLanding && (!incomingCode || !isStandardStorefront) && !userLoading && !isLoggedIn) {
      void openModal(ModalNames.signUp);
    }
    const warningKey = `${userId ?? ''}:${dispensaryId ?? ''}:${incomingCode}`;
    const wasAttempted =
      priorAttemptedCode === incomingCode ||
      attemptedKeyRef.current === warningKey ||
      (matchesSession && session?.attemptedCode === incomingCode && key === warningKey);
    const sessionReady =
      !incomingCode ||
      !isStandardStorefront ||
      (!!session && session.dispensaryId === dispensaryId && session.code === incomingCode);
    if (
      onReferralLanding &&
      sessionReady &&
      isLoggedIn &&
      rewards.isLoyaltyMember &&
      !wasAttempted &&
      warnedKeyRef.current !== warningKey
    ) {
      warnedKeyRef.current = warningKey;
      showErnie('Since you already have an account, the referral reward is not available.', ERNIE_TYPES.DANGER);
    }
  }, [
    onReferralLanding,
    isStandardStorefront,
    incomingCode,
    userLoading,
    isLoggedIn,
    userId,
    dispensaryId,
    rewards.isLoyaltyMember,
    priorAttemptedCode,
    matchesSession,
    session,
    key,
    openModal,
    showErnie,
  ]);

  useEffect(() => {
    if (matchesSession && session?.status === 'pending' && isLoggedIn && rewardsSettled && hasNativeProfile) {
      saveSession({ ...session, status: 'closed' });
    }
  }, [matchesSession, session, isLoggedIn, rewardsSettled, hasNativeProfile, saveSession]);

  // Opt-in retailers still use their existing explicit Connect/Terms flows, unchanged.
  const autoEnrollEligible =
    flags['growth.ecomm.referral-auto-enrollment.rollout'] === true &&
    isStandardStorefront &&
    !!referralCode &&
    session?.status === 'pending' &&
    !session.attemptedCode &&
    !priorAttemptedCode &&
    isLoggedIn &&
    !!userId &&
    !userLoading &&
    rewardsSettled &&
    !hasNativeProfile &&
    supportsReferral &&
    rewards.loyaltyConfig?.loyaltyRequireOptIn === false;
  const [findReferral] = useMutation(FindOrCreateReferralCodeDocument);
  const lookupRef = useRef<ResolvedReferral['lookup'] | null>(null);
  const [resolved, setResolved] = useState<ResolvedReferral | null>(null);
  const resolvedReferral = resolved?.lookup.key === key ? resolved.data : null;
  const { createUserAndPin } = useLoyalty({
    initialStatus: Statuses.loading,
    // Join Loyalty still reads the legacy dispensary hook, which can lag the shell.
    integration: enrollmentStoreReady ? Integrations.dutchiepos : Integrations.none,
    surface: null,
    referralData: resolvedReferral ?? null,
  });

  useEffect(() => {
    if (!autoEnrollEligible || !dispensaryId || lookupRef.current?.key === key) {
      return undefined;
    }
    const lookup = { key };
    lookupRef.current = lookup;
    let active = true;
    void findReferral({
      variables: { dispensaryId, lspName: lspName ?? '', code: referralCode },
    }).then(
      ({ data }) => {
        if (active) {
          setResolved({ lookup, data: data?.findOrCreateReferralCode });
        }
      },
      () => {
        // No enrollment was submitted; a later dependency change/revisit can resolve again.
        if (lookupRef.current === lookup) {
          lookupRef.current = null;
        }
      }
    );
    return () => {
      active = false;
      if (lookupRef.current === lookup) {
        lookupRef.current = null;
      }
    };
  }, [autoEnrollEligible, dispensaryId, lspName, referralCode, key, findReferral]);

  useEffect(() => {
    if (!autoEnrollEligible || resolved?.lookup !== lookupRef.current || resolved.lookup.key !== key) {
      return;
    }
    if (
      !lspName?.trim() ||
      resolvedReferral?.code !== referralCode ||
      resolvedReferral.lspName !== lspName ||
      !resolvedReferral.senderPosUniqueId
    ) {
      saveSession({ ...session, status: 'closed' });
      return;
    }
    if (attemptedKeyRef.current === key) {
      return;
    }
    // Keep the attempt separate: navigation may clear or replace pending attribution.
    setPersistedValue(attemptStorageKey, referralCode, true);
    attemptedKeyRef.current = key;
    saveSession({ ...session, status: 'attempted', attemptedCode: referralCode });
    void createUserAndPin({ silent: true });
  }, [
    autoEnrollEligible,
    attemptStorageKey,
    session,
    resolved?.lookup,
    key,
    lspName,
    resolvedReferral,
    referralCode,
    saveSession,
    createUserAndPin,
  ]);
}
